interface LocationLike {
  search: string;
  [key: string]: unknown;
}

function isLocation(obj: unknown): obj is LocationLike {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  const keys = Object.keys(obj);
  if (!keys.includes('hash')) {
    return false;
  }
  if (!keys.includes('key')) {
    return false;
  }
  if (!keys.includes('pathname')) {
    return false;
  }

  return true;
}

const reforms: Record<string, (v: string) => unknown> = {
  order: (v) => v,
  orderBy: (v) => v,
  filter: (v) => v,
  filterKey: (v) => v,
  headerFilters: (v) => JSON.parse(v),
  dateRange: (v) => JSON.parse(v),
  id: (v) => Number(v),
  userId: (v) => Number(v),
  userIndex: (v) => Number(v),
  receivingId: (v) => Number(v),
  receivingIndex: (v) => Number(v),
  orderId: (v) => Number(v),
  orderIndex: (v) => Number(v),
  inventoryOrderId: (v) => Number(v),
  inventoryOrderIndex: (v) => Number(v),
  bulkOrderId: (v) => Number(v),
  bulkOrderIndex: (v) => Number(v),
  collectionId: (v) => Number(v),
  collectionIndex: (v) => Number(v),
  productId: (v) => Number(v),
  productIndex: (v) => Number(v),
  duplicate: (v) => Number(v),
  invoiceNumber: (v) => v,
  invoiceIndex: (v) => Number(v),
  pageSize: (v) => Number(v),
};

type URLSearchParamsExOptions = string | LocationLike | Record<string, unknown>;

// the URLSearchParams class doesnt support
// nested object as its constructor options
// and it cannot handle the difference type for each query parameter
export default class URLSearchParamsEx {
  urlParams: URLSearchParams = new URLSearchParams();

  values: Record<string, unknown> = {};

  constructor(options: URLSearchParamsExOptions) {
    if (typeof options === 'string') {
      // URLSearchParams only strips one leading '?'. Strip extras to handle
      // malformed URLs like '??order=asc' that result in '?order' as a key.
      this.urlParams = new URLSearchParams(options.replace(/^\?+/, ''));
    } else if (isLocation(options)) {
      // Same guard for location.search which may have a double leading '??'
      // when navigation code concatenates '?' with a search string that already starts with '?'.
      this.urlParams = new URLSearchParams(options.search.replace(/^\?+/, ''));
    } else if (typeof options === 'object') {
      this.urlParams = new URLSearchParams();
      Object.keys(options).forEach((key) => {
        const raw = options[key];
        const val = typeof raw === 'object' && raw !== null ? JSON.stringify(raw) : String(raw);
        this.urlParams.append(key, val);
      });
    } else {
      throw new Error('unknown params type');
    }

    this.#toObject();
  }

  set(k: string, v: string | ((current: unknown) => string)) {
    if (typeof v === 'function') {
      const currentValue = this.values[k];
      const nextValue = v(currentValue);
      this.urlParams.set(k, nextValue);
    } else {
      this.urlParams.set(k, v);
    }

    this.#toObject();
  }

  delete(k: string) {
    this.urlParams.delete(k);
    this.#toObject();
  }

  toString(): string {
    return this.urlParams.toString();
  }

  #toObject() {
    this.values = {};
    this.urlParams.forEach((v, k) => {
      const reform = reforms[k];
      if (!reform) {
        throw new Error(`unknown reform: ${k}`);
      }

      this.values[k] = reform(v);
    });
  }
}
